import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from './cn'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** ローディング中。aria-busy を付与し操作を抑止する */
  loading?: boolean
  children?: ReactNode
}

// 共通: フォーカス可視リング・無効/角丸・トランジション。
// 角丸 control=10px・disabled 半透明はブランド既定（トークン参照）。
const BASE =
  'inline-flex items-center justify-center gap-2 font-medium rounded-control ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-primary focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

// variant マップ: 色・枠は variant が確定する（利用側 className での上書き対象外）
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-hover',
  secondary:
    'bg-white text-primary border-[1.5px] border-primary hover:bg-primary hover:text-white',
  ghost: 'bg-transparent text-text hover:bg-black/5',
  danger: 'bg-danger text-white hover:bg-danger-hover',
}

// size マップ: md は既定サイズ。縦 padding は py-2.5（10px）でボタン高さ約44px（Unit 004 でコンパクト化）
const SIZE: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-8 py-2.5 text-base',
  lg: 'px-10 py-4 text-lg',
}

/** アクショントリガー。type 既定は button。 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, disabled, type, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(BASE, VARIANT[variant], SIZE[size], className)}
      {...rest}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  )
})
